<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-8 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("app_catalog.stock_unmatched_title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-4 col-5 text-right">
                    <base-button size="md" type="neutral" @click="loadList">
                        <i class="fa fa-sync"></i> {{ $t("refresh") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <h3 class="mb-0">
                        {{ $t("app_catalog.stock_unmatched_count", { n: count }) }}
                    </h3>
                    <p class="text-muted small mb-0">{{ $t("app_catalog.stock_unmatched_hint") }}</p>
                    <!-- An endpoint that is not deployed yet must not look like an
                         empty, healthy catalogue. BE-3 ships the handler; until then
                         (and on any 4xx/5xx) the page says so instead of rendering
                         "ไม่พบข้อมูล" under a count of 0. -->
                    <div v-if="loadFailed" class="alert alert-warning py-2 px-3 mt-2 mb-0">
                        <i class="fa fa-exclamation-triangle mr-1"></i>
                        {{ $t("app_catalog.stock_unmatched_load_failed") }}
                    </div>
                    <!-- The candidate scan is capped (§8). When it bites, the
                         count above is a lower bound and saying nothing would
                         read as "that is all of them". -->
                    <div v-else-if="truncated" class="alert alert-warning py-2 px-3 mt-2 mb-0">
                        <i class="fa fa-exclamation-triangle mr-1"></i>
                        {{ $t("app_catalog.stock_unmatched_truncated") }}
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table
                        :data="rows"
                        row-key="skuCode"
                        size="small"
                        header-row-class-name="thead-light"
                        :empty-text="$t('app_catalog.stock_unmatched_empty')"
                    >
                        <el-table-column :label="$t('app_catalog.stock_unmatched_col_product')" min-width="200px">
                            <template slot-scope="{ row }">
                                <!-- The §8 response is keyed by sku_code and need
                                     not carry a product id. Without one there is
                                     nothing to link to, so the cell renders what it
                                     has as text — an <a href=""> here would reload
                                     the very page the operator is working through. -->
                                <router-link v-if="editPath(row)" :to="editPath(row)">
                                    {{ row.productName || ("#" + row.productId) }}
                                </router-link>
                                <span v-else class="text-muted">{{ row.productName || "-" }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.sku_code')" min-width="180px">
                            <template slot-scope="{ row }">
                                <span class="sku-code-cell">{{ row.skuCode }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.stock_unmatched_col_reason')" min-width="150px">
                            <template slot-scope="{ row }">
                                <el-tag :type="row.code === 'wms_unmapped' ? 'info' : 'warning'" size="mini">
                                    {{ $t(reasonKey(row)) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.stock_unmatched_col_missing')" min-width="200px">
                            <template slot-scope="{ row }">
                                <span v-if="row.parts.length > 0">{{ row.parts.join(", ") }}</span>
                                <span v-else-if="row.missing > 0" class="text-muted small">
                                    {{ $t("app_catalog.stock_unmatched_missing_count", { n: row.missing }) }}
                                </span>
                                <span v-else class="text-muted small">
                                    {{ $t("app_catalog.wms_unmatched_tooltip_unknown") }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.stock_unmatched_col_message')" min-width="220px">
                            <template slot-scope="{ row }">
                                <span class="text-muted small">{{ row.message }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" min-width="90px" align="right">
                            <template slot-scope="{ row }">
                                <base-button
                                    v-if="editPath(row)"
                                    size="sm"
                                    type="warning"
                                    @click="$router.push(editPath(row))"
                                >
                                    <i class="fa fa-edit"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <template slot="footer">
                    <div class="d-flex justify-content-end mt-3">
                        <el-pagination
                            background
                            layout="prev, pager, next"
                            :total="total"
                            :page-size="params.limit"
                            :current-page="params.page"
                            @current-change="onPageChange"
                        ></el-pagination>
                    </div>
                </template>
            </card>
        </div>
    </div>
</template>

<script>
// #301 / shared stock FE-1 — "SKU ที่ยังไม่ได้แมปคลัง"
// (docs/specs/2026-10-01-shared-stock-design.md §8, §11 FE-1).
//
// WHY THIS PAGE EXISTS. Once `shared_mode: write` is on, a SKU whose wms_map
// resolves completely has its storefront quantity pushed from the warehouse. A
// SKU whose map is EMPTY or points at something unusable does not (spec Q3/Q6):
// it keeps whatever staff typed, forever, with no indication on the shop that
// its number is frozen. §12 item 2 is explicit that somebody has to work through
// that list before write mode is worth switching on — the per-product badge on
// /app-catalog/products (#302) cannot be that list, because it only speaks about
// the 20 rows of the page in hand.
//
// Deliberately read-only and deliberately dumb: every rule about the payload
// (which envelopes to accept, which rows to drop, how a missing product id is
// rendered) lives in @/utils/stockSource, where it is unit-tested, because this
// repo's jest cannot mount a .vue file (reference-bo-fn-jest-infra).
import { apiErrorMessage } from "@/util/api-error";
import { Table, TableColumn, Tag, Pagination } from "element-ui";
import { AppCatalogProductService } from "@/resource";
import {
    normaliseUnmatchedRows,
    unmatchedBannerCount,
    unmatchedRowI18nKey,
    unmatchedEditPath
} from "@/utils/stockSource";

export default {
    name: "AppCatalogStockUnmatched",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Pagination.name]: Pagination
    },
    data() {
        return {
            loading: false,
            loadFailed: false,
            truncated: false,
            rows: [],
            count: 0,
            total: 0,
            params: {
                page: 1,
                limit: 50
            }
        };
    },
    mounted() {
        this.loadList();
    },
    methods: {
        async loadList() {
            this.loading = true;
            this.loadFailed = false;
            this.truncated = false;
            try {
                const res = await AppCatalogProductService.stockUnmatched({ ...this.params });
                const body = (res && res.data) || {};
                this.rows = normaliseUnmatchedRows(body);
                this.count = unmatchedBannerCount(body);
                this.total = this.pagerTotal(body, this.rows.length);
                // §8's scan cap. Said out loud, because the alternative is a
                // census that silently under-reports: an operator who works
                // through the list and sees the count stop moving has no way to
                // tell "done" from "the scan never looked that far".
                this.truncated = body.scan_truncated === true;
            } catch (err) {
                this.rows = [];
                this.count = 0;
                this.total = 0;
                this.truncated = false;
                this.loadFailed = true;
                this.$notify({ type: "danger", message: apiErrorMessage(err, this.$t) });
            } finally {
                this.loading = false;
            }
        },
        // The pager's total.
        //
        // `total` first, for any BE that sends one. Then `count`, WHICH IS THE
        // REAL TOTAL HERE: BE-3 classifies every candidate row and only then
        // slices the page, so `count` is the number of warnings across the whole
        // scan (Catalog_stock_unmatched.go) and `scan_truncated` says when even
        // that is a lower bound. The first version of this method refused `count`
        // on the grounds that it counts missing warehouse CODES — true of
        // `wms_unmatched_count` on the product LIST (#302), not of this body — and
        // fell back to "(page-1)*limit + fetched", which equals one page exactly,
        // so element-ui disabled the next-page button and pages 2+ were
        // unreachable on any tenant with more unmapped SKUs than one page holds.
        //
        // The fallback is kept for a body that carries neither (an older or
        // stubbed endpoint): one page, which is honest about what was fetched.
        pagerTotal(body, fetched) {
            const t = Number(body && body.total);
            if (isFinite(t) && t > 0) return Math.floor(t);
            const c = Number(body && body.count);
            if (isFinite(c) && c > 0 && body && body.limit !== undefined) return Math.floor(c);
            return (this.params.page - 1) * this.params.limit + fetched;
        },
        reasonKey(row) {
            return unmatchedRowI18nKey(row);
        },
        editPath(row) {
            return unmatchedEditPath(row);
        },
        onPageChange(page) {
            this.params.page = page;
            this.loadList();
        }
    }
};
</script>

<style scoped>
/* Combo codes ("A+B+C") must wrap inside the cell instead of widening it. */
.sku-code-cell {
    word-break: break-word;
}
</style>
